import React from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import {
  Box,
  Typography as MuiTypography,
  Divider as MuiDivider,
  Grid,
  Alert,
  TextField as MuiTextField,
  CircularProgress as MuiCircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAccount,
  updateStickyLocation,
  updateStickySession,
} from "../../../../redux/slices/account";
import { toast } from "react-hot-toast";

// Styled Components
const Divider = styled(MuiDivider)(spacing);
const Typography = styled(MuiTypography)(spacing);
const TitleTypography = styled(Typography)`
  margin-top: 15px;
  padding-right: 20px;
  padding-bottom: 12px;
  color: ${(props) => props.theme.sidebar.header.color};
`;
const Wrapper = styled.div`
  padding-left: 10px;
  padding-right: 10px;
`;

const Ptypography = styled(Typography)`
  margin-top: 15px;
  padding-right: 20px;
`;

const TextField = styled(MuiTextField)`
  margin-top: 5px;
  margin-right: 5px;
  padding: 11px;
`;

const CircularProgress = styled(MuiCircularProgress)(spacing);

const AccountSettings = () => {
  const dispatch = useDispatch();
  const fetchAccountData = useSelector(
    (state) => state.account.fetchAccountData
  );
  const [stickyLocation, setStickyLocation] = React.useState(false);
  const [stickySession, setStickySession] = React.useState(false);
  const [accountData, setAccountData] = React.useState({});
  const role = localStorage.getItem("atur"); // Assuming "atur" stores the role
  const isAdmin = role === "638f1029a1c0997b2085c2a2";

  React.useEffect(() => {
    dispatch(fetchAccount());
  }, [dispatch]);

  React.useEffect(() => {
    if (fetchAccountData?.status === "success" && fetchAccountData?.data) {
      setAccountData(fetchAccountData.data);
    }
  }, [fetchAccountData?.status, fetchAccountData?.data]);

  React.useEffect(() => {
    if (fetchAccountData?.data) {
      if (fetchAccountData?.data?.sticky_location !== undefined) {
        setStickyLocation(fetchAccountData.data.sticky_location);
      }
      if (fetchAccountData?.data?.sticky_session !== undefined) {
        setStickySession(fetchAccountData.data.sticky_session);
      }
    }
  }, [fetchAccountData]);

  const handleStickyLocationChange = async (event) => {
    const newValue = event.target.checked;
    try {
      await dispatch(updateStickyLocation(newValue)).unwrap();
      await dispatch(fetchAccount()).unwrap();
      setStickyLocation(newValue);
      toast.success(
        `Sticky Location ${newValue ? "enabled" : "disabled"} successfully`
      );
    } catch (error) {
      toast.error("Failed to update sticky location setting");
    }
  };

  const handleStickySessionChange = async (event) => {
    const newValue = event.target.checked;
    try {
      await dispatch(updateStickySession(newValue)).unwrap();
      await dispatch(fetchAccount()).unwrap();
      setStickySession(newValue);
      toast.success(
        `Sticky session ${newValue ? "enabled" : "disabled"} successfully`
      );
    } catch (error) {
      toast.error("Failed to update sticky session setting");
    }
  };

  return (
    <Box
      // style={{
      //   backgroundColor: "#F9F9F9",
      //   height: "92vh",
      //   borderRight: "1px solid rgba(0, 0, 0, 0.12)",
      // }}

      sx={{
        backgroundColor: "#F9F9F9",
        height: "92vh",
        borderRight: "1px solid rgba(0, 0, 0, 0.12)",
        overflowY: "auto", // ✅ scrollbar
        overflowX: "hidden",
        pr: 1, // optional: avoids scrollbar overlap
      }}
    >
      <Wrapper>
        <TitleTypography variant="h4">
          <SettingsSuggestIcon /> Account Settings
        </TitleTypography>
      </Wrapper>
      <Divider my={2} sx={{ borderBottomWidth: 2 }} />
      <Wrapper>
        <Grid container>
          <Grid item xs={12} lg={9}>
            <Box
              style={{
                backgroundColor: "#F9F9F9",
                height: "92vh",
                borderRight: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              {fetchAccountData?.status === "loading" && (
                <CircularProgress m={2} color="primary" />
              )}
              <Grid container>
                <Grid item xs={12} lg={4}>
                  <Typography gutterBottom variant="h5" component="h2">
                    General settings
                  </Typography>
                </Grid>
                <Grid item xs={12} lg={8}>
                  <Ptypography component="p">
                    <b>Account Name</b>
                  </Ptypography>
                  <TextField
                    name="account_name"
                    value={accountData?.name || ""}
                    fullWidth
                    type="text"
                    variant="filled"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        height: 36, // control overall height
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    }}
                  />
                  {/* 
                  <Ptypography component="p">
                    Conversation continuity with emails is enabled for your account.
                  </Ptypography> */}

                  <Ptypography component="p">
                    <b>
                      Number of days after a ticket should auto resolve if there
                      is no activity
                    </b>
                  </Ptypography>
                  <TextField
                    name="resolve"
                    value={accountData?.auto_resolve_duration || ""}
                    fullWidth
                    type="number"
                    variant="filled"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        height: 36, // control overall height
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    }}
                  />
                  {/* <Ptypography component="p">
                    <b>Enable Location Settings</b>
                  </Ptypography>
                  <FormControlLabel
                    control={
                      <Tooltip
                        title={
                          stickyLocation
                            ? "Disable location settings"
                            : "Enable location settings"
                        }
                      >
                        <Switch
                          checked={stickyLocation}
                          onChange={handleStickyLocationChange}
                          color="primary"
                          name="sticky_location"
                        />
                      </Tooltip>
                    }
                    label={stickyLocation ? "Enabled" : "Disabled"}
                    sx={{ mt: 2, mb: 2 }}
                  />
                  <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                    When enabled, Location Settings will be active, and you can
                    manage location-based settings. When disabled, Location
                    Settings will not be available.
                  </Alert> */}

                  {/* updated by nikhil at 2025-04-25 ========================== */}
                  {isAdmin && (
                    <>
                      <>
                        <Ptypography component="p">
                          <b>Enable Location Settings</b>
                        </Ptypography>
                        <FormControlLabel
                          control={
                            <Tooltip
                              title={
                                stickyLocation
                                  ? "Disable location settings"
                                  : "Enable location settings"
                              }
                            >
                              <Switch
                                checked={stickyLocation}
                                onChange={handleStickyLocationChange}
                                color="primary"
                                name="sticky_location"
                              />
                            </Tooltip>
                          }
                          label={stickyLocation ? "Enabled" : "Disabled"}
                          sx={{ mt: 2, mb: 2 }}
                        />
                      </>
                      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        Toggles location-based settings on or off.
                      </Alert>
                    </>
                  )}

                  {/* <Ptypography component="p">
                    <b>Sticky Session</b>
                  </Ptypography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={stickySession}
                        onChange={handleStickySessionChange}
                        color="primary"
                        name="sticky_session"
                      />
                    }
                    label={stickySession ? "Enabled" : "Disabled"}
                    sx={{ mt: 2, mb: 1 }}
                  />
                  <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                    User can enable or disable Sticky Session based on their
                    requirement.
                  </Alert> */}

                  {isAdmin && (
                    <>
                      <>
                        <Typography component="p">
                          <b>Sticky Session</b>
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={stickySession}
                              onChange={handleStickySessionChange}
                              color="primary"
                              name="sticky_session"
                            />
                          }
                          label={stickySession ? "Enabled" : "Disabled"}
                          sx={{ mt: 2, mb: 1 }}
                        />
                      </>
                      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                        User can enable or disable Sticky Session based on their
                        requirement.
                      </Alert>
                    </>
                  )}
                </Grid>
              </Grid>
              <Divider />
              <Grid container my={5}>
                <Grid item xs={12} lg={4}>
                  <Typography gutterBottom variant="h5" component="h2">
                    Account ID
                  </Typography>
                  <Ptypography component="p">
                    This ID is required if you are building an API-based
                    integration.
                  </Ptypography>
                </Grid>
                <Grid item xs={12} lg={8}>
                  <Ptypography component="p">
                    <b>Account ID</b>
                  </Ptypography>
                  <TextField
                    name="AccountID"
                    value={accountData?._id || ""}
                    fullWidth
                    type="text"
                    variant="filled"
                    size="small"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        height: 36, // control overall height
                        paddingTop: 0,
                        paddingBottom: 0,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Box
              style={{
                padding: "10px",
                backgroundColor: "#F9F9F9",
                height: "92vh",
              }}
            >
              <Typography gutterBottom variant="h5" component="h2">
                Account Settings
              </Typography>
              <Ptypography component="p">
                Manage your account settings, including enabling or disabling
                sticky location and session settings.
              </Ptypography>
              <Ptypography component="p">
                Sticky Location allows you to manage location-based settings,
                while Sticky Session enables session management.
              </Ptypography>
              <Ptypography component="p">
                Use the toggles to enable or disable these features as per your
                requirements.
              </Ptypography>
            </Box>
          </Grid>
        </Grid>
      </Wrapper>
    </Box>
  );
};

export default AccountSettings;
